/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

class ReflowEvent extends Event {
  /**
   * @param {HTMLElement} lastVisibleElement - The element to move to the last visible position
   */
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section, it needs to be render-blocking to avoid layout shifts.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    // Add event listener for reflow requests
    this.addEventListener(
      'reflow',
      /** @param {ReflowEvent} event */ (event) => {
        this.#reflowItems(event.detail.lastVisibleElement);
      }
    );

    if (this.hasAttribute('defer')) {
      const deferredReflow = () => {
        // Remove attribute first to change layout before calculating the actual size
        this.removeAttribute('defer');
        this.#reflowItems();
      };

      const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

      idleCallback(() => this.schedule(deferredReflow));
    } else {
      this.#reflowItems();
    }
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : requestAnimationFrame;
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @type {{width: number | null, height: number | null}}
   */
  #lastDimensions = {
    width: null,
    height: null,
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = (event) => {
    if (this.#scheduled) return;

    let width = null;
    let height = null;
    let isResize = false;

    for (const [, entry] of event.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      // There should only be one entry
      isResize = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }

    if (isResize) {
      if (!width || !height || (width === this.#lastDimensions.width && height === this.#lastDimensions.height)) {
        // Skip reflow if dimensions are 0 or the same as the last reflow
        return;
      }

      this.#lastDimensions = { width: Math.round(width), height: Math.round(height) };
    }

    this.#scheduled = true;

    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Reflow items based on available space within the list.
   * @param {HTMLElement} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (lastVisibleElement) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    // Avoid layout shifts while reflowing the list
    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};

    if (height) list.style.height = `${height}px`;
    list.style.setProperty('overflow', 'hidden');

    // Move all elements to the default slot so we can check which ones overflow
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();

    // Make sure the "More" item and placeholder are hidden
    moreSlot.hidden = true;
    placeholder.hidden = true;

    // First, check if all the items fit
    const rootRect = list.getBoundingClientRect();

    // Store the initial dimensions for comparison later
    this.#lastDimensions = { width: Math.round(rootRect.width), height: Math.round(rootRect.height) };

    const getVisibleElements = () => elements.filter((el) => el.getBoundingClientRect().top <= rootRect.top);
    let visibleElements = getVisibleElements();

    // If not all items fit or we have a lastVisibleElement, let's calculate with "More" button
    if (visibleElements.length !== elements.length || lastVisibleElement) {
      // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;
      moreSlot.style.setProperty('height', `${height}px`);

      if (lastVisibleElement) {
        lastVisibleElement.style.setProperty('order', '-1');
      }

      // Recalculate the visible elements
      visibleElements = getVisibleElements();

      // Reset the order
      moreSlot.style.removeProperty('order');
      if (lastVisibleElement) {
        lastVisibleElement.style.removeProperty('order');
      }

      // If we have a lastVisibleElement, ensure it's in the last visible position
      if (lastVisibleElement) {
        const visibleIndex = visibleElements.indexOf(lastVisibleElement);
        if (visibleIndex !== -1) {
          // Remove lastVisibleElement from its current position
          visibleElements.splice(visibleIndex, 1);
          // Add it to the end of visible elements
          visibleElements.push(lastVisibleElement);
        }
      }

      moreSlot.style.setProperty('height', 'auto');
    }

    const overflowingElements = elements.filter((element) => !visibleElements.includes(element));
    const [firstOverflowingElement] = overflowingElements;
    const hasOverflow = overflowingElements.length > 0;
    const placeholderWidth = firstOverflowingElement ? firstOverflowingElement.clientWidth : 0;

    // Move the elements to the correct slot
    for (const element of elements) {
      element.slot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
    }

    // If there are overflowing elements
    // Show more button and placeholder if needed
    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      // Set the width and height of the placeholder so the list can grow if there is space
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    list.style.removeProperty('overflow');

    // Check if the minimum-reached state should be updated
    hasOverflow && this.#updateMinimumReached(visibleElements);

    // Observe the list for changes in size
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

// Function to calculate total height of header group children
function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }
  return totalHeight;
}

/**
 * Initialize and maintain header height CSS variables.
 * This is critical for preventing layout shifts during page load.
 * There is a `ResizeObserver` and `MutationObserver` that kicks in post hydration in header.js
 * Note: header-group uses display: contents, so we must observe all children.
 */
(() => {
  const header = document.querySelector('header-component');

  // Early exit if no header - nothing to do
  if (!(header instanceof HTMLElement)) return;

  // Calculate initial height(s
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);

  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
})();
/* === BUTIK GLOBAL JS === */
/*
  Butik Madame A — Global Theme JS
  assets/butik-global.js
  Runs on EVERY page:
    1. Replaces logo with correct brand logo everywhere
    2. Injects animated gold SVG paths on every page
    3. Polishes cart count, interactions
*/

(function () {
  'use strict';

  /* ── 1. LOGO REPLACEMENT ───────────────────────────── */
  var _logoSrc = null;

  function applyLogoToImg(img) {
    if (!_logoSrc) return;
    if (img.classList.contains('resource-card__image') ||
        img.classList.contains('resource-card__placeholder') ||
        img.classList.contains('predictive-search__image')) return;
    if (img.closest('dialog, [class*="predictive-search"], [class*="search-result"], [class*="resource-item"], [class*="resource-card"]')) return;
    var alt = (img.alt || '').toLowerCase();
    var src = (img.src || '').toLowerCase();
    var srcPath = src.replace(/^https?:\/\/[^/]+/, '');
    var isLogo = alt.includes('butik') || alt.includes('madame') || alt.includes('logo') ||
                 srcPath.includes('logookrag') || srcPath.includes('logo') || srcPath.includes('madame') ||
                 (img.closest('#header-group, header, #footer-group, footer') && img.offsetHeight < 150);
    if (isLogo && img.src !== _logoSrc) {
      img.src = _logoSrc;
      img.srcset = '';
      img.sizes = '';
      img.style.cssText += ';max-height:52px!important;width:auto!important;object-fit:contain!important;';
    }
  }

  function replaceLogo() {
    var meta = document.querySelector('meta[name="butik-logo"]');
    if (!meta) return;
    _logoSrc = meta.getAttribute('content');
    if (!_logoSrc) return;
    document.querySelectorAll(
      '#header-group img:not(dialog *), header img:not(dialog *), ' +
      '#footer-group img:not(dialog *), footer img:not(dialog *), ' +
      '[class*="logo"] img:not(dialog *)'
    ).forEach(applyLogoToImg);
  }

  function watchLogo() {
    if (!window.MutationObserver) return;
    var obs = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.closest && node.closest('dialog')) return;
          if (node.tagName === 'IMG') applyLogoToImg(node);
          node.querySelectorAll && node.querySelectorAll('img:not(dialog *)').forEach(applyLogoToImg);
        });
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  function replaceLogo_withRetry() {
    replaceLogo();
    [300, 800, 1500, 3000].forEach(function (ms) {
      setTimeout(replaceLogo, ms);
    });
    watchLogo();
  }

  /* ── 2. ANIMATED GOLD PATHS (all pages) ────────────── */
  var NS = 'http://www.w3.org/2000/svg';

  function buildPath(i, pos) {
    var a = -380 - i * 5 * pos, b = -189 + i * 6,
        c = -312 - i * 5 * pos, d =  216 - i * 6,
        e =  152 - i * 5 * pos, f =  343 - i * 6,
        g =  616 - i * 5 * pos, h =  470 - i * 6,
        k =  684 - i * 5 * pos, m =  875 - i * 6;
    return 'M' + a + ' ' + b +
           ' C' + a + ' ' + b + ' ' + c + ' ' + d + ' ' + e + ' ' + f +
           ' C' + g + ' ' + h + ' ' + k + ' ' + m + ' ' + k + ' ' + m;
  }

  function makeSvg(position) {
    var svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '0 0 1440 900');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    svg.setAttribute('fill', 'none');
    svg.style.overflow = 'hidden';
    svg.style.pointerEvents = 'none';
    for (var i = 0; i < 32; i++) {
      var path = document.createElementNS(NS, 'path');
      path.setAttribute('d', buildPath(i, position));
      path.setAttribute('stroke', '#c9a45c');
      path.setAttribute('stroke-width', String(0.4 + i * 0.025));
      path.setAttribute('fill', 'none');
      path.style.opacity = String(Math.min(0.04 + i * 0.014, 0.35));
      svg.appendChild(path);
    }
    return svg;
  }

  function injectPaths() {
    if (document.getElementById('paths-svg-1')) return;
    if (document.querySelector('.bm-paths-wrap')) return;

    var wrap = document.createElement('div');
    wrap.className = 'bm-paths-wrap';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.pointerEvents = 'none';
    wrap.appendChild(makeSvg(1));
    wrap.appendChild(makeSvg(-1));

    document.body.appendChild(wrap);
  }

  /* ── 3. CART COUNT SYNC ─────────────────────────────── */
  function syncCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('.cart-badge, [class*="cart-count"], [class*="cart-bubble"]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function () {});
  }

  /* ── 4. COOKIE CONSENT SHADOW DOM STYLING ──────────── */
  var CONSENT_CSS = [
    '*, *::before, *::after { box-sizing: border-box; }',
    ':host > *, [class*="banner"], [class*="container"], [class*="wrapper"], [class*="modal"], div[role="dialog"] {',
    '  background: linear-gradient(135deg, #1e1d1b 0%, #252320 100%) !important;',
    '  border: 1px solid rgba(201,164,92,.25) !important;',
    '  border-radius: 6px !important;',
    '  color: rgba(255,255,255,.87) !important;',
    '  box-shadow: 0 12px 50px rgba(0,0,0,.8) !important;',
    '  font-family: "Bricolage Grotesque", sans-serif !important;',
    '}',
    'p, span, li { color: rgba(255,255,255,.6) !important; font-size: .8rem !important; line-height: 1.7 !important; }',
    'h1, h2, h3 { color: #fff !important; font-family: "Bricolage Grotesque", sans-serif !important; }',
    'a { color: #c9a45c !important; }',
    'button:not([class*="secondary"]):not([class*="decline"]):not([class*="manage"]) {',
    '  background: #c9a45c !important; color: #1a1918 !important;',
    '  border: none !important; padding: .7rem 1.5rem !important;',
    '  font-weight: 700 !important; cursor: pointer !important; border-radius: 2px !important;',
    '  font-family: "Space Mono", monospace !important; font-size: .58rem !important;',
    '  letter-spacing: .14em !important; text-transform: uppercase !important;',
    '  transition: background .2s ease !important;',
    '}',
    'button[class*="secondary"], button[class*="decline"], button[class*="manage"], button:last-child {',
    '  background: transparent !important; color: rgba(255,255,255,.5) !important;',
    '  border: 1px solid rgba(201,164,92,.3) !important; padding: .65rem 1.2rem !important;',
    '  cursor: pointer !important; border-radius: 2px !important; font-size: .58rem !important;',
    '  letter-spacing: .1em !important; text-transform: uppercase !important;',
    '}'
  ].join('\n');

  function styleConsentBanner() {
    var selectors = [
      'shopify-consent-banner', 'privacy-consent',
      'shopify-pc-banner', '[data-shopify-consent]',
      'consent-api'
    ];
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.shadowRoot && !el.__bmStyled) {
          var s = document.createElement('style');
          s.textContent = CONSENT_CSS;
          el.shadowRoot.appendChild(s);
          el.__bmStyled = true;
        }
      });
    });
  }

  function watchConsentBanner() {
    var attempts = 0;
    var iv = setInterval(function () {
      styleConsentBanner();
      if (++attempts > 20) clearInterval(iv);
    }, 300);
    if (!window.MutationObserver) return;
    new MutationObserver(function () { styleConsentBanner(); })
      .observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ── 5. HEADER SCROLL SHADOW ───────────────────────── */
  function initHeaderScroll() {
    var header = document.getElementById('header-group');
    if (!header) return;
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          header.classList.toggle('scrolled', window.scrollY > 30);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ── 6. SMOOTH IMAGE LOAD ───────────────────────────── */
  function lazyImageFade() {
    if (!('IntersectionObserver' in window)) return;
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.style.opacity = '1';
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      img.style.transition = 'opacity .4s ease';
      if (!img.complete) {
        img.style.opacity = '0';
        img.addEventListener('load', function () { img.style.opacity = '1'; });
      }
      obs.observe(img);
    });
  }

  /* ── ACTIVE NAV LINK + GOLD HOVER ───────────────── */
  function markActiveNav() {
    var path = window.location.pathname;
    var selectors = 'header a, #header-group a, [class*="mobile-menu"] a, [class*="drawer"] a, [class*="nav"] a';
    document.querySelectorAll(selectors).forEach(function (a) {
      if (a.closest('#bm-nav-logo-wrap')) return;
      var href = a.getAttribute('href');
      if (!href || href === '#') return;
      var isActive = href === path || (href.length > 1 && path.startsWith(href));
      if (isActive) {
        a.style.setProperty('color', '#c9a45c', 'important');
        a.style.setProperty('font-weight', '700', 'important');
      }
    });
  }

  function applyGoldNavHover() {
    document.querySelectorAll('#header-group a').forEach(function (a) {
      if (a.closest('#bm-nav-logo-wrap')) return;
      if (a._bmHover) return;
      a._bmHover = true;
      a.addEventListener('mouseenter', function () {
        a.style.setProperty('color', '#c9a45c', 'important');
      });
      a.addEventListener('mouseleave', function () {
        a.style.removeProperty('color');
        if (a.getAttribute('href') === window.location.pathname) {
          a.style.color = '#c9a45c';
        }
      });
    });
  }

  /* ── 7a. MOBILE MENU — logo hide / show ────────────── */
  function watchMobileMenu() {
    var logo = document.getElementById('bm-nav-logo-wrap');
    if (!logo) return;

    function setLogoVisible(visible) {
      logo.style.opacity = visible ? '' : '0';
      logo.style.pointerEvents = visible ? '' : 'none';
    }

    new MutationObserver(function() {
      var menuOpen = isAnyDrawerOpen();
      setLogoVisible(!menuOpen);
    }).observe(document.documentElement, {
      attributes: true, attributeFilter: ['scroll-lock']
    });

    var menuDrawer = document.querySelector('menu-drawer');
    if (menuDrawer) {
      new MutationObserver(function() {
        setLogoVisible(!menuDrawer.hasAttribute('open'));
      }).observe(menuDrawer, { attributes: true, attributeFilter: ['open'] });
    }

    var detailsMenu = document.querySelector('details.menu-drawer-container');
    if (detailsMenu) {
      new MutationObserver(function() {
        setLogoVisible(!detailsMenu.hasAttribute('open'));
      }).observe(detailsMenu, { attributes: true, attributeFilter: ['open'] });
    }
  }

  /* ── 7. SCROLL-LOCK GUARD ───────────────────────────── */
  var _scrollLockSetAt = 0;

  function isAnyDrawerOpen() {
    return !!(
      document.querySelector('cart-drawer[open]') ||
      document.querySelector('menu-drawer[open]') ||
      document.querySelector('details[open].menu-drawer-container') ||
      document.querySelector('[class*="drawer"][open]') ||
      document.querySelector('[class*="modal"].open') ||
      document.querySelector('[class*="modal"].active') ||
      document.querySelector('[aria-modal="true"]')
    );
  }

  function releaseScrollIfSafe() {
    if (Date.now() - _scrollLockSetAt < 800) return;
    if (!isAnyDrawerOpen()) {
      document.documentElement.removeAttribute('scroll-lock');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
  }

  function installScrollGuard() {
    if (window.MutationObserver) {
      new MutationObserver(function (muts) {
        muts.forEach(function (m) {
          if (m.attributeName === 'scroll-lock') {
            if (document.documentElement.hasAttribute('scroll-lock')) {
              _scrollLockSetAt = Date.now();
            } else {
              _scrollLockSetAt = 0;
            }
          }
        });
      }).observe(document.documentElement, { attributes: true, attributeFilter: ['scroll-lock'] });
    }

    window.addEventListener('load', function () {
      setTimeout(releaseScrollIfSafe, 1000);
    });
  }

  /* ── 7b. INJECT CART BUTTON ON ALL PRODUCT CARDS ─── */
  function injectCartButtons() {
    var nativeBtn = document.querySelector('quick-add-button button, .quick-add__submit, .card__add-button');
    var refSize = nativeBtn ? nativeBtn.getBoundingClientRect().width : 0;
    var btnSize = refSize > 10 ? Math.round(refSize) + 'px' : '46px';

    document.querySelectorAll('.card-wrapper, [class*="card-wrapper"]').forEach(function (card) {
      if (card.querySelector('.bm-cart-btn')) return;
      if (card.querySelector('quick-add-button, .quick-add__submit, .card__add-button')) return;

      var link = card.querySelector('a[href*="/products/"]');
      if (!link) return;

      var imgWrap = card.querySelector('[class*="card__media"], [class*="card__image"], .card__media, .media');
      if (!imgWrap) return;

      var btn = document.createElement('a');
      btn.className = 'bm-cart-btn';
      if (nativeBtn) {
        nativeBtn.classList.forEach(function(c) {
          if (c !== 'quick-add__submit') btn.classList.add(c);
        });
      }
      btn.href = link.href;
      btn.setAttribute('aria-label', 'Dodaj do koszyka');
      btn.style.width = btnSize;
      btn.style.height = btnSize;

      var nativeIcon = nativeBtn && nativeBtn.querySelector('svg');
      if (nativeIcon) {
        btn.appendChild(nativeIcon.cloneNode(true));
      } else {
        btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/><line x1="17" y1="3" x2="17" y2="9"/><line x1="14" y1="6" x2="20" y2="6"/></svg>';
      }

      imgWrap.style.position = 'relative';
      imgWrap.appendChild(btn);
    });
  }

  /* ── 8. INJECT CENTRED NAV LOGO ────────────────────── */
  function injectCenteredNavLogo() {
    if (document.getElementById('bm-nav-logo-wrap')) return;

    var meta = document.querySelector('meta[name="butik-logo"]');
    if (!meta) return;
    var src = meta.getAttribute('content');
    if (!src) return;

    var headerEl = document.querySelector('#header-group, header-component');
    var headerH = headerEl ? Math.max(headerEl.offsetHeight, 44) : 64;

    var isHome = window.location.pathname === '/' || window.location.pathname === '';
    var logoPaddingTop = isHome ? '16px' : '0px';

    var wrap = document.createElement('div');
    wrap.id = 'bm-nav-logo-wrap';
    wrap.style.cssText = [
      'position:fixed', 'top:0', 'left:0', 'right:0',
      'height:' + headerH + 'px',
      'display:flex', 'align-items:center', 'justify-content:center',
      'padding-top:' + logoPaddingTop,
      'pointer-events:none',
      'z-index:9999'
    ].join(';');

    var a = document.createElement('a');
    a.id = 'bm-nav-logo';
    a.href = '/';
    a.setAttribute('aria-label', 'Strona Główna');
    var logoMargin = isHome ? '0px' : '-44px';
    a.style.cssText = 'pointer-events:auto;display:flex;align-items:center;text-decoration:none;margin-top:' + logoMargin + ';';

    var img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.style.cssText = 'height:44px;width:auto;max-height:44px;object-fit:contain;display:block;transition:opacity .2s ease,transform .2s cubic-bezier(.34,1.56,.64,1);';
    img.addEventListener('mouseover', function () { img.style.opacity = '.85'; img.style.transform = 'scale(1.05)'; });
    img.addEventListener('mouseout',  function () { img.style.opacity = '1';   img.style.transform = 'scale(1)';    });

    a.appendChild(img);
    wrap.appendChild(a);
    document.body.appendChild(wrap);
  }

  function injectCenteredNavLogo_withRetry() {
    injectCenteredNavLogo();
    [200, 600, 1200].forEach(function (ms) {
      setTimeout(injectCenteredNavLogo, ms);
    });
  }

  /* ── HERO BUTTONS STYLE ─────────────────────────────── */
  function styleHeroButtons() {
    var gold = '#c9a45c', goldLight = '#e2d69a';

    document.querySelectorAll('.btn-tiktok-style').forEach(function(btn) {
      if (btn._bmStyled) return;
      btn._bmStyled = true;
      btn.style.cssText += ';background:' + gold + '!important;color:#1a1918!important;border:1.5px solid ' + gold + '!important;border-radius:3px!important;box-shadow:0 4px 20px rgba(201,164,92,.35)!important;transition:background .2s ease,transform .18s cubic-bezier(.34,1.56,.64,1),box-shadow .2s ease!important;text-decoration:none!important;';
      btn.addEventListener('mouseenter', function() {
        btn.style.setProperty('background', goldLight, 'important');
        btn.style.setProperty('border-color', goldLight, 'important');
        btn.style.setProperty('transform', 'translateY(-4px)', 'important');
        btn.style.setProperty('box-shadow', '0 10px 32px rgba(201,164,92,.45)', 'important');
      });
      btn.addEventListener('mouseleave', function() {
        btn.style.setProperty('background', gold, 'important');
        btn.style.setProperty('border-color', gold, 'important');
        btn.style.removeProperty('transform');
        btn.style.setProperty('box-shadow', '0 4px 20px rgba(201,164,92,.35)', 'important');
      });
    });

    document.querySelectorAll('.btn-facebook-style').forEach(function(btn) {
      if (btn._bmStyled) return;
      btn._bmStyled = true;
      btn.style.cssText += ';background:#1a1816!important;color:' + gold + '!important;border:1.5px solid rgba(201,164,92,.55)!important;border-radius:3px!important;box-shadow:none!important;transition:background .2s ease,border-color .2s ease,color .2s ease,transform .18s cubic-bezier(.34,1.56,.64,1),box-shadow .2s ease!important;text-decoration:none!important;';
      btn.addEventListener('mouseenter', function() {
        btn.style.setProperty('background', 'rgba(201,164,92,.15)', 'important');
        btn.style.setProperty('border-color', gold, 'important');
        btn.style.setProperty('color', gold, 'important');
        btn.style.setProperty('transform', 'translateY(-4px)', 'important');
        btn.style.setProperty('box-shadow', '0 8px 28px rgba(201,164,92,.25)', 'important');
      });
      btn.addEventListener('mouseleave', function() {
        btn.style.setProperty('background', '#1a1816', 'important');
        btn.style.setProperty('border-color', 'rgba(201,164,92,.55)', 'important');
        btn.style.setProperty('color', gold, 'important');
        btn.style.removeProperty('transform');
        btn.style.setProperty('box-shadow', 'none', 'important');
      });
    });
  }

  /* ── INIT ────────────────────────────────────────────── */
  function init() {
    replaceLogo_withRetry();
    injectCenteredNavLogo_withRetry();
    injectPaths();
    syncCartCount();
    lazyImageFade();
    styleHeroButtons();
    [300, 800].forEach(function (ms) { setTimeout(styleHeroButtons, ms); });
    markActiveNav();
    [400, 1200].forEach(function (ms) { setTimeout(markActiveNav, ms); });
    applyGoldNavHover();
    [300, 900].forEach(function (ms) { setTimeout(applyGoldNavHover, ms); });
    injectCartButtons();
    [500, 1500].forEach(function (ms) { setTimeout(injectCartButtons, ms); });
    watchConsentBanner();
    installScrollGuard();
    initHeaderScroll();
    watchMobileMenu();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', replaceLogo_withRetry);
  document.addEventListener('page:load', init);
  document.addEventListener('turbo:load', init);

})();

/* ── HOMEPAGE DOM RESTRUCTURE ──────────────────────────────
   The compiled theme serves a frozen snapshot. We use JS
   to restructure the homepage DOM to match the reference.
─────────────────────────────────────────────────────────── */
(function() {
  function restructureHomepage() {
    /* Only run on homepage */
    if (window.location.pathname !== '/' && window.location.pathname !== '') return;

    /* 1. Fix trust strip: replace 4 items with 3 correct items */
    var ts = document.querySelector('.trust-strip');
    if (ts) {
      ts.innerHTML = [
        '<div class="trust-item">',
          '<div class="trust-icon">',
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">',
              '<circle cx="12" cy="12" r="10"/>',
              '<polyline points="12 6 12 12 16 14"/>',
            '</svg>',
          '</div>',
          '<div class="trust-item-label">Wysyłka w ciągu 24h<strong>Błyskawiczna realizacja zamówienia</strong></div>',
        '</div>',
        '<div class="trust-item">',
          '<div class="trust-icon">',
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">',
              '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.95-.86a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>',
            '</svg>',
          '</div>',
          '<div class="trust-item-label">Obsługa na poziomie<strong>Odpowiadamy 7 dni w tygodniu</strong></div>',
        '</div>',
        '<div class="trust-item">',
          '<div class="trust-icon">',
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">',
              '<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>',
              '<path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
            '</svg>',
          '</div>',
          '<div class="trust-item-label">Bezpieczna płatność<strong>Szyfrowane transakcje online</strong></div>',
        '</div>'
      ].join('');
      /* Update grid to 3 columns */
      ts.style.gridTemplateColumns = 'repeat(3, 1fr)';
    }

    /* 2. Move trust strip to AFTER collections section */
    var kolekcje = document.getElementById('kolekcje');
    if (kolekcje && ts && ts.parentNode) {
      kolekcje.parentNode.insertBefore(ts, kolekcje.nextSibling);
    }

    /* 3. Remove USP strip */
    var uspStrip = document.querySelector('.usp-strip');
    if (uspStrip) uspStrip.remove();

    /* 4. Fix brand section labels */
    var brandEyebrow = document.querySelector('#o-nas .sec-eyebrow');
    if (brandEyebrow && brandEyebrow.textContent.includes('Historia')) {
      brandEyebrow.textContent = 'O Nas';
    }
    var brandTitle = document.querySelector('#o-nas .sec-title');
    if (brandTitle && brandTitle.textContent.includes('Stworzona')) {
      brandTitle.innerHTML = 'Założona<br>z Pasją do Mody';
    }
    /* Fix brand stats */
    var brandStats = document.querySelector('#o-nas .brand-stats');
    if (brandStats) {
      var statItems = brandStats.querySelectorAll('div');
      if (statItems.length >= 3) {
        statItems[0].querySelector('.stat-val') && (statItems[0].querySelector('.stat-val').textContent = '5 TYS.+');
        statItems[0].querySelector('.stat-lbl') && (statItems[0].querySelector('.stat-lbl').textContent = 'Zadowolonych Klientek');
        statItems[1].querySelector('.stat-val') && (statItems[1].querySelector('.stat-val').textContent = '99%');
        statItems[1].querySelector('.stat-lbl') && (statItems[1].querySelector('.stat-lbl').textContent = 'Pozytywnych Opinii');
        if (statItems[2] && statItems[2].querySelector) statItems[2].remove();
      }
    }

    /* 5. Fix products section eyebrow */
    var nowosciEyebrow = document.querySelector('#nowosci .sec-eyebrow');
    if (nowosciEyebrow && nowosciEyebrow.textContent.includes('Arrivals')) {
      nowosciEyebrow.textContent = 'Prosto do Butiku';
    }

    /* 6. Add social media section before newsletter — only if not already in Liquid */
    var nlSection = document.querySelector('.nl-section');
    var fbUrl = 'https://www.facebook.com/butikmadamea/?locale=pl_PL';
    var ttUrl = 'https://www.tiktok.com/@_butik_madame_a_';
    if (nlSection && !document.getElementById('bm-social-section') && !document.querySelector('.social-section')) {
      var socHtml = '<section id="bm-social-section" style="background:var(--bg-dark);border-top:1px solid var(--gold-border);padding:5rem var(--page-pad,4rem);text-align:center">' +
        '<div class="sec-eyebrow centered" style="font-family:Space Mono,monospace;font-size:.55rem;letter-spacing:.22em;text-transform:uppercase;color:var(--gold);margin-bottom:.75rem">Dołącz do Społeczności</div>' +
        '<h2 style="font-family:Bricolage Grotesque,sans-serif;font-size:clamp(1.6rem,3.5vw,2.4rem);font-weight:700;color:#fff;margin:0 auto 1rem;letter-spacing:-.02em">Obserwuj <em style="color:var(--gold);font-style:normal">Butik Madame A</em></h2>' +
        '<p style="color:var(--text-muted,rgba(255,255,255,.5));margin:0 auto 2.5rem;max-width:520px;font-size:.82rem;line-height:1.7">Bądź na bieżąco z nowościami, inspiracjami i ekskluzywnymi ofertami.</p>' +
        '<div style="display:flex;justify-content:center;gap:1.5rem;flex-wrap:wrap">' +
          '<a href="' + ttUrl + '" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:.6rem;padding:.85rem 2.2rem;background:#111;color:#fff;border:1.5px solid rgba(255,255,255,.2);border-radius:3px;font-family:Space Mono,monospace;font-size:.6rem;letter-spacing:.15em;text-transform:uppercase;font-weight:700;text-decoration:none;transition:transform .18s cubic-bezier(.34,1.56,.64,1)">' +
            '<svg viewBox="0 0 24 24" fill="currentColor" stroke="none" width="18" height="18"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34l-.02-8.62a8.16 8.16 0 004.77 1.52V7.74a4.85 4.85 0 01-.98-.05z"/></svg>' +
            'TikTok' +
          '</a>' +
          '<a href="' + fbUrl + '" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:.6rem;padding:.85rem 2.2rem;background:#c9a45c;color:#1a1918;border:1.5px solid #c9a45c;border-radius:3px;font-family:Space Mono,monospace;font-size:.6rem;letter-spacing:.15em;text-transform:uppercase;font-weight:700;text-decoration:none;transition:transform .18s cubic-bezier(.34,1.56,.64,1)">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>' +
            'Facebook' +
          '</a>' +
        '</div>' +
      '</section>';
      nlSection.insertAdjacentHTML('beforebegin', socHtml);
    }

    /* 7. Move newsletter BEFORE testimonials (currently reviews come before nl) */
    var opinie = document.getElementById('opinie');
    if (nlSection && opinie && opinie.parentNode) {
      var nlNext = nlSection.nextElementSibling;
      if (nlNext !== opinie) {
        opinie.parentNode.insertBefore(nlSection, opinie);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restructureHomepage);
  } else {
    restructureHomepage();
  }
  document.addEventListener('page:load', restructureHomepage);
  document.addEventListener('turbo:load', restructureHomepage);
})();
